import React, { useCallback, useEffect, useState } from "react";

import { Button, Icon, Label, Message, Modal } from "semantic-ui-react";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import ModConfirm from "react-lib/apps/common/cnmi/ModConfirm";

import {
  MeLabPptxStatus,
  MeLabReportSummary,
  PPTX_STATUS_NEEDS_REVIEW,
  RunSequence,
} from "react-lib/apps/HISV3/LAB/sequence/LabReport";
import { formatLocalizedDateTime } from "react-lib/utils/dateUtils";

// ======================= Constants =======================
const ACTION = "GENERATE_MELAB_PPTX";
const BUTTON_KEY = "ButtonMeLabPptx_GENERATE";
const REGENERATE_KEY = "ButtonMeLabPptx_REGENERATE";
const APPROVE_KEY = "ButtonMeLabPptx_APPROVE";

// ======================= Types =======================
type ButtonMeLabPptxProps = {
  card: string;
  setProp: (key: string, value: any) => any;
  runSequence: RunSequence;
  // data
  labOrdersReported: any[];
  patientInfo?: Record<string, any>;
  selectedLabOrderWorking?: Record<string, any>;
  meLabReport?: MeLabReportSummary | null;
  meLabPptxStatus?: Record<string, MeLabPptxStatus>;
  buttonLoadCheck?: Record<string, any>;
  // config
  disabled?: boolean;
};

// ======================= Helpers =======================
// ชื่อ (หรือ id ถ้าไม่มีชื่อ) ตามด้วยเวลา
const formatActor = (name?: string | null, id?: string | null, at?: string | null) =>
  [name || id || "ไม่ระบุ", at ? formatLocalizedDateTime(at) : ""].filter(Boolean).join(" ");

// ======================= Component =======================
const ButtonMeLabPptx = (props: ButtonMeLabPptxProps) => {
  const [openModal, setOpenModal] = useState(false);
  const [statusChecked, setStatusChecked] = useState(false);
  const [needsReviewReasons, setNeedsReviewReasons] = useState<string[] | null>(null);
  // key ของปุ่มที่กดสร้างล่าสุด
  const [generateKey, setGenerateKey] = useState(BUTTON_KEY);
  const [openRegenerateConfirm, setOpenRegenerateConfirm] = useState(false);
  const [openApproveConfirm, setOpenApproveConfirm] = useState(false);

  const reportId = props.meLabReport?.report_id;
  const pptx = reportId ? props.meLabPptxStatus?.[reportId] : undefined;
  const isGenerated = !!pptx?.generated;
  const isApproved = !!pptx?.pptx_approved_at;
  const isRegenerating = props.buttonLoadCheck?.[REGENERATE_KEY] === "LOADING";
  const isGenerating = props.buttonLoadCheck?.[BUTTON_KEY] === "LOADING" || isRegenerating;

  useEffect(() => {
    setStatusChecked(false);

    if (!reportId) {
      return;
    }

    props.runSequence({
      sequence: "LabReport",
      action: "FETCH_MELAB_PPTX_STATUS",
      reportId,
      onSettled: () => setStatusChecked(true),
    });
  }, [reportId]);

  const handleGenerate = useCallback(
    (buttonLoadKey: string, allowNeedsReview?: boolean) => {
      setGenerateKey(buttonLoadKey);

      props.runSequence({
        sequence: "LabReport",
        action: ACTION,
        card: props.card,
        buttonLoadKey,
        labOrdersReported: props.labOrdersReported,
        patientInfo: props.patientInfo,
        selectedLabOrderWorking: props.selectedLabOrderWorking,
        reportId,
        allowNeedsReview,
        onSuccess: () => {
          setNeedsReviewReasons(null);
          setOpenRegenerateConfirm(false);
        },
        onNeedsReview: (reasons: string[]) => {
          setOpenRegenerateConfirm(false);
          setNeedsReviewReasons(reasons);
        },
      });
    },
    [
      props.card,
      props.labOrdersReported,
      props.patientInfo,
      props.selectedLabOrderWorking,
      reportId,
    ],
  );

  const handleRegenerate = useCallback(() => {
    if (isApproved) {
      setOpenRegenerateConfirm(true);
    } else {
      handleGenerate(REGENERATE_KEY);
    }
  }, [isApproved, handleGenerate]);

  const handleApprove = useCallback(() => {
    if (!reportId) {
      return;
    }

    props.runSequence({
      sequence: "LabReport",
      action: "APPROVE_MELAB_PPTX",
      card: props.card,
      buttonLoadKey: APPROVE_KEY,
      reportId,
      onSuccess: () => setOpenApproveConfirm(false),
    });
  }, [props.card, reportId]);

  const handleDownload = useCallback(() => {
    if (pptx?.pptx_output_url) {
      window.open(pptx.pptx_output_url, "_blank");
    }
  }, [pptx?.pptx_output_url]);

  const handleCloseNeedsReview = useCallback(() => setNeedsReviewReasons(null), []);

  // ยังไม่ได้ fetch report
  if (props.meLabReport === undefined) {
    return null;
  }

  if (reportId && !statusChecked && !pptx && !isGenerating) {
    return (
      <Button
        disabled
        size="medium"
        color="grey"
        style={{ width: "100%", minWidth: "max-content" }}
      >
        <Icon name="circle notched" loading />
        สไลด์
      </Button>
    );
  }

  const renderStatusRow = (label: string, value: React.ReactNode) => (
    <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
      <div style={{ minWidth: "7rem", fontWeight: "bold" }}>{label}</div>
      <div>{value}</div>
    </div>
  );

  return (
    <>
      <ButtonLoadCheck
        setProp={props.setProp}
        onClick={isGenerated ? () => setOpenModal(true) : () => handleGenerate(BUTTON_KEY)}
        paramKey={BUTTON_KEY}
        buttonLoadCheck={props.buttonLoadCheck?.[BUTTON_KEY]}
        disabled={(!reportId && props.disabled) || isRegenerating}
        size="medium"
        name={ACTION}
        color={isApproved ? "olive" : "purple"}
        style={{ width: "100%", minWidth: "max-content" }}
        title={
          isGenerating ? (
            "กำลังสร้าง..."
          ) : isGenerated ? (
            <>
              {isApproved && <Icon name="check circle" />}
              ดูสไลด์
            </>
          ) : (
            "สร้างสไลด์"
          )
        }
      />

      {isGenerated && (
        <Modal
          className="centered"
          open={openModal}
          size="small"
          closeIcon
          onClose={() => setOpenModal(false)}
        >
          <Modal.Header>สไลด์อธิบายผลตรวจ</Modal.Header>

          <Modal.Content>
            {pptx?.labmap_status === PPTX_STATUS_NEEDS_REVIEW && (
              <Message warning size="small">
                สไลด์นี้เป็นแบบร่าง ข้อมูลผลตรวจไม่ครบหรือขัดกัน ควรตรวจทานก่อนอนุมัติ
              </Message>
            )}

            {renderStatusRow(
              "สถานะ",
              isApproved ? (
                <Label color="olive">อนุมัติแล้ว</Label>
              ) : (
                <Label color="purple">รออนุมัติ</Label>
              ),
            )}
            {renderStatusRow(
              "สูตร",
              [pptx?.labmap_formula_id, pptx?.labmap_formula_version].filter(Boolean).join(" ") ||
                "-",
            )}
            {renderStatusRow(
              "สร้างโดย",
              formatActor(
                pptx?.pptx_generated_by_name,
                pptx?.pptx_generated_by,
                pptx?.pptx_generated_at,
              ),
            )}
            {isApproved &&
              renderStatusRow(
                "อนุมัติโดย",
                formatActor(
                  pptx?.pptx_approved_by_name,
                  pptx?.pptx_approved_by,
                  pptx?.pptx_approved_at,
                ),
              )}
          </Modal.Content>

          <Modal.Actions>
            <Button onClick={() => setOpenModal(false)}>ปิด</Button>

            <Button color="blue" onClick={handleDownload}>
              <Icon name="download" />
              ดาวน์โหลด
            </Button>

            <Button
              color="purple"
              loading={isRegenerating}
              disabled={isGenerating}
              onClick={handleRegenerate}
            >
              สร้างใหม่
            </Button>

            {!isApproved && (
              <Button
                color="green"
                disabled={isGenerating}
                onClick={() => setOpenApproveConfirm(true)}
              >
                อนุมัติสไลด์
              </Button>
            )}
          </Modal.Actions>
        </Modal>
      )}

      <ModConfirm
        openModal={openRegenerateConfirm}
        centered
        titleName="สร้างสไลด์ใหม่"
        size="mini"
        approveButtonText="สร้างใหม่"
        approveButtonColor="purple"
        denyButtonText="ยกเลิก"
        content={
          <div style={{ margin: "0.5rem 0" }}>
            สไลด์นี้อนุมัติแล้ว สร้างใหม่จะยกเลิกการอนุมัติเดิมและต้องอนุมัติใหม่ ใช่หรือไม่
          </div>
        }
        approveButtonLoading={isRegenerating}
        onApprove={() => handleGenerate(REGENERATE_KEY)}
        onDeny={() => setOpenRegenerateConfirm(false)}
        onCloseWithDimmerClick={() => setOpenRegenerateConfirm(false)}
      />

      <ModConfirm
        openModal={openApproveConfirm}
        centered
        titleName="ยืนยันการอนุมัติ"
        size="mini"
        approveButtonText="อนุมัติ"
        denyButtonText="ยกเลิก"
        content={<div style={{ margin: "0.5rem 0" }}>อนุมัติสไลด์อธิบายผลตรวจใช่หรือไม่</div>}
        approveButtonLoading={props.buttonLoadCheck?.[APPROVE_KEY] === "LOADING"}
        onApprove={handleApprove}
        onDeny={() => setOpenApproveConfirm(false)}
        onCloseWithDimmerClick={() => setOpenApproveConfirm(false)}
      />

      <ModConfirm
        openModal={!!needsReviewReasons}
        centered
        titleName="ข้อมูลผลตรวจต้องตรวจทาน"
        size="tiny"
        approveButtonText="สร้างแบบร่าง"
        approveButtonColor="orange"
        denyButtonText="ยกเลิก"
        content={
          <div style={{ margin: "0.5rem 0" }}>
            ข้อมูลผลตรวจไม่ครบหรือขัดกัน ต้องการสร้างสไลด์เป็นแบบร่างหรือไม่
            {!!needsReviewReasons?.length && (
              <ul style={{ marginBottom: 0 }}>
                {needsReviewReasons.map((reason, index) => (
                  <li key={`reason-${index}`}>{reason}</li>
                ))}
              </ul>
            )}
          </div>
        }
        approveButtonLoading={isGenerating}
        onApprove={() => handleGenerate(generateKey, true)}
        onDeny={handleCloseNeedsReview}
        onCloseWithDimmerClick={handleCloseNeedsReview}
      />
    </>
  );
};

ButtonMeLabPptx.displayName = "ButtonMeLabPptx";

export default React.memo(ButtonMeLabPptx);
